// Word Chain interface. The chain is a column of words; the first and last are shown, and each hidden word shows its
// first letter and a square for every other letter. Tap a word (or move with the arrow keys) and type, with the
// device's own keyboard (a tap focuses a hidden field, which opens it on a phone) or a real one. A word is checked as
// soon as its last square is filled: a right word turns green and the compound words it makes are shown; a wrong one
// is marked so it can be erased. Reveal letter gives the next letter; every change can be undone. The chain is in the
// server HTML (games/boards/_word_chain.html.erb), so nothing moves on load. The hidden-field handling follows
// cryptogram/ui.ts.
import { compounds, eraseLetter, hidden, isFull, isRight, isSolved, revealLetter, startEntries, typeLetter, type Entries, type Puzzle, type WCLevel } from "./core"
import { readJSON, writeJSON } from "../lib/store"
import { formatLong, isToday, mark } from "../lib/daily"
import { gameEvent } from "../lib/game-events"
import { gameProgress, newAttempt } from "../lib/stats"

export interface WordChainDaily { game: string; date: string }
interface Saved { v: 1; puzzle: Puzzle; entries: Entries; attempt: number; undo: Entries[]; cleared?: Entries | null }
const PREF_KEY = "dt:pref:word-chain-level"

export class WordChainGame {
  private p!: Puzzle
  private e!: Entries
  private undo: Entries[] = []
  private cleared: Entries | null = null
  private attempt = 0
  private wonAttempt: string | null = null
  private cur = 1 // the word being typed
  private hints = 0
  private sentFirst = false
  private composing = ""
  private readonly status = document.querySelector<HTMLElement>("[data-status]")
  private readonly chain: HTMLElement
  private readonly input: HTMLInputElement | null
  private readonly saveKey: (l: WCLevel) => string

  constructor(private readonly root: HTMLElement, private readonly daily?: WordChainDaily) {
    this.saveKey = (l) => (daily ? `dt:save:daily:${daily.game}:${daily.date}:v1` : `dt:save:word-chain-${l}:v1`)
    this.chain = root.querySelector<HTMLElement>("[data-wc-chain]")!
    this.input = root.querySelector<HTMLInputElement>("[data-wc-input]")
    this.input?.addEventListener("input", (ev) => this.onInput(ev as InputEvent))
    this.resetInput()
    const ssr = JSON.parse(root.dataset.puzzle ?? "{}") as Puzzle
    const pref = daily ? null : readPref()
    const saved = readJSON<Saved>(this.saveKey(pref ?? ssr.level))
    let switchTo: WCLevel | null = null
    if (saved?.v === 1 && saved.puzzle?.words && (!daily || saved.puzzle.id === ssr.id)) {
      this.load(saved)
      if (!isSolved(this.p, this.e)) this.say("Welcome back. Your chain is where you left it.")
    } else {
      this.p = ssr
      this.e = startEntries(ssr)
      this.attempt = newAttempt()
      if (pref && pref !== ssr.level) switchTo = pref
    }
    if (daily) root.querySelector('[data-act="new"]')?.remove()
    this.cur = this.firstOpen(1)
    document.querySelector("[data-wc-levels]")?.addEventListener("click", (ev) => {
      const l = (ev.target as HTMLElement).closest<HTMLElement>("[data-level]")?.dataset.level as WCLevel | undefined
      if (l) void this.loadLevel(l)
    })
    root.addEventListener("click", (ev) => this.onClick(ev))
    root.addEventListener("mousedown", (ev) => { if ((ev.target as HTMLElement).closest("[data-w]")) ev.preventDefault() })
    document.addEventListener("keydown", (ev) => this.onKey(ev))
    this.rebuild()
    if (switchTo) void this.loadLevel(switchTo)
  }

  private load(s: Saved): void {
    this.p = s.puzzle; this.e = s.entries; this.attempt = s.attempt; this.undo = s.undo ?? []; this.cleared = s.cleared ?? null
  }

  // ---- lifecycle ---------------------------------------------------------------------------------------------
  private async loadLevel(level: WCLevel, avoid?: string): Promise<void> {
    if (this.sentFirst && !isSolved(this.p, this.e)) gameEvent({ type: "end", ruleset: `word-chain:${this.p.level}`, outcome: "new_game", hints: this.hints, deal: this.p.id })
    this.sentFirst = false; this.hints = 0
    try {
      const saved = readJSON<Saved>(this.saveKey(level))
      if (saved?.v === 1 && saved.puzzle?.words && !isSolved(saved.puzzle, saved.entries) && saved.puzzle.id !== avoid) {
        this.load(saved)
      } else {
        const bank = (await (await fetch(`/puzzles/word-chain/${level}.json`)).json()) as Puzzle[]
        const choices = bank.filter((b) => b.id !== avoid && b.id !== this.p.id)
        this.p = choices[Math.floor(Math.random() * choices.length)] ?? bank[0]!
        this.e = startEntries(this.p)
        this.undo = []; this.cleared = null
        this.attempt = newAttempt()
      }
      writePref(level)
      this.cur = this.firstOpen(1)
      this.rebuild()
      this.persist()
      this.say(`${cap(level)} chain: ${this.p.words[0]} to ${this.p.words[this.p.words.length - 1]}, ${hidden(this.p).length} words to find. ${this.where()}`)
    } catch {
      this.say("Couldn't load a chain. Check your connection and try again.")
    }
  }

  private persist(): void {
    writeJSON(this.saveKey(this.p.level), { v: 1, puzzle: this.p, entries: this.e, attempt: this.attempt, undo: this.undo.slice(-300), cleared: this.cleared } satisfies Saved)
  }

  // ---- input -------------------------------------------------------------------------------------------------
  private onClick(ev: Event): void {
    const t = ev.target as HTMLElement
    const word = t.closest<HTMLElement>("[data-w]")
    if (word) {
      const i = Number(word.dataset.w)
      if (hidden(this.p).includes(i)) { this.choose(i); this.openKeyboard() }
      return
    }
    const act = t.closest<HTMLElement>("[data-act]")?.dataset.act
    if (act === "reveal-letter") this.reveal()
    else if (act === "undo") this.undoLast()
    else if (act === "restart") this.restart()
    else if (act === "new") void this.loadLevel(this.p.level, this.p.id)
  }

  private onKey(ev: KeyboardEvent): void {
    if (ev.ctrlKey || ev.metaKey || ev.altKey) {
      if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === "z" && !this.inField(ev)) { ev.preventDefault(); this.undoLast() }
      return
    }
    if (this.inField(ev) || document.querySelector("[data-finish]:not([hidden])")?.contains(ev.target as Node)) return
    const onBoard = this.root.contains(ev.target as Node) || ev.target === document.body
    if (!onBoard) return
    if (/^[a-zA-Z]$/.test(ev.key)) { ev.preventDefault(); return this.type(ev.key.toUpperCase()) }
    const h = hidden(this.p)
    const at = h.indexOf(this.cur)
    if (ev.key === "ArrowDown" || ev.key === "ArrowRight") { ev.preventDefault(); return this.choose(h[Math.min(h.length - 1, at + 1)]!) }
    if (ev.key === "ArrowUp" || ev.key === "ArrowLeft") { ev.preventDefault(); return this.choose(h[Math.max(0, at - 1)]!) }
    if (ev.key === "Backspace" || ev.key === "Delete") { ev.preventDefault(); return this.erase() }
    if (ev.key === "Enter") { ev.preventDefault(); return this.choose(this.firstOpen(this.cur + 1)) }
  }

  private inField(ev: Event): boolean {
    const t = ev.target as HTMLElement
    return t !== this.input && !!t.closest?.("input, select, textarea")
  }

  private openKeyboard(): void {
    if (!this.input || isSolved(this.p, this.e)) return
    this.placeInput()
    this.input.focus({ preventScroll: true })
    this.resetInput()
  }
  private placeInput(): void {
    const word = this.wordEl(this.cur)
    const main = this.input?.offsetParent
    if (!word || !main || !this.input) return
    const a = word.getBoundingClientRect(), b = main.getBoundingClientRect()
    this.input.style.top = `${Math.round(a.top - b.top + a.height / 2)}px`
    this.input.style.left = `${Math.round(a.left - b.left + a.width / 2)}px`
  }
  private resetInput(): void {
    if (!this.input) return
    this.input.value = " "
    try { this.input.setSelectionRange(1, 1) } catch { /* not focusable yet */ }
  }
  /** Letters from a phone keyboard; see CrosswordGame.onInput for why composed words are handled this way. */
  private onInput(ev?: InputEvent): void {
    const v = this.input!.value
    this.resetInput()
    const kind = ev?.inputType ?? ""
    if (kind === "insertCompositionText" || (kind === "insertText" && this.composing)) {
      const word = (ev?.data ?? "").toUpperCase().replace(/[^A-Z]/g, "")
      const was = this.composing
      this.composing = kind === "insertText" ? "" : word
      if (kind === "insertText" && word === was) return
      if (word.length > was.length && word.startsWith(was)) { for (const ch of word.slice(was.length)) this.type(ch); return }
      if (word.length < was.length && was.startsWith(word)) { for (let i = word.length; i < was.length; i++) this.erase(); return }
      for (const ch of word) this.type(ch)
      return
    }
    this.composing = ""
    if (v.length < 1) return this.erase()
    for (const ch of v.toUpperCase().replace(/[^A-Z]/g, "")) this.type(ch)
  }

  private choose(i: number): void {
    this.cur = i
    this.render()
    this.focusWord()
    this.say(this.where())
  }

  private type(letter: string): void {
    if (isSolved(this.p, this.e)) return this.say("This chain is finished. Choose New puzzle to play another.")
    const i = this.cur
    if (isRight(this.p, this.e, i)) return this.say(`${this.e[i]} is already right. Choose another word.`)
    if (isFull(this.p, this.e, i)) return this.say(`${this.e[i]} doesn't fit. Press Backspace to change it.`)
    this.change(typeLetter(this.p, this.e, i, letter))
    if (!this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset: `word-chain:${this.p.level}`, daily: !!this.daily }) }
    this.after(i)
  }

  private erase(): void {
    const i = this.cur
    if (isRight(this.p, this.e, i)) return this.say(`${this.e[i]} is right, so it stays. Choose another word.`)
    const next = eraseLetter(this.e, i)
    if (next === this.e) return this.say(`${this.where()} The first letter is given.`)
    this.change(next)
    this.render()
    this.focusWord()
    this.say(this.where())
  }

  private change(next: Entries): void {
    this.undo.push(this.e)
    this.e = next
    this.persist()
  }

  // ---- tools -------------------------------------------------------------------------------------------------
  private reveal(): void {
    if (isSolved(this.p, this.e)) return this.say("This chain is finished.")
    if (isRight(this.p, this.e, this.cur)) this.cur = this.firstOpen(this.cur)
    const i = this.cur
    this.hints++
    if (!this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset: `word-chain:${this.p.level}`, daily: !!this.daily }) }
    this.change(revealLetter(this.p, this.e, i))
    this.after(i)
  }

  private undoLast(): void {
    const last = this.undo.pop()
    if (!last) {
      if (this.cleared) {
        this.e = this.cleared; this.cleared = null
        this.persist(); this.render()
        return this.say("Your words are back.")
      }
      return this.say("Nothing to undo.")
    }
    this.e = last
    this.persist()
    this.render()
    this.focusWord()
    this.say("Undone.")
  }

  private restart(): void {
    const fresh = startEntries(this.p)
    if (fresh.every((w, i) => w === this.e[i])) return this.say("There's nothing to clear yet.")
    this.cleared = this.e
    this.e = fresh
    this.undo = []
    this.attempt = newAttempt()
    this.sentFirst = false; this.hints = 0
    this.cur = this.firstOpen(1)
    this.persist()
    this.render()
    this.say("Restarted. Undo brings back your words.")
  }

  /** After a change to word i: say whether it's right, wrong or still being typed, and move on when it's right. */
  private after(i: number): void {
    if (isSolved(this.p, this.e)) {
      this.render()
      const key = `${this.p.id}#${this.attempt}`
      if (this.wonAttempt !== key) { this.wonAttempt = key; gameEvent({ type: "end", ruleset: `word-chain:${this.p.level}`, outcome: "win", hints: this.hints, deal: this.p.id, daily: !!this.daily }) }
      if (this.daily) mark(this.daily.game, this.daily.date, "won")
      this.input?.blur()
      const said = `${this.e.join(", ")}.`
      return this.say(this.daily
        ? isToday(this.daily.date) ? `You finished today's Word Chain! ${said} Come back tomorrow for a new one.` : `You finished the Word Chain for ${formatLong(this.daily.date)}! ${said}`
        : `You finished the chain! ${said} Choose New puzzle to play another.`)
    }
    if (this.daily) mark(this.daily.game, this.daily.date, "played")
    let note = ""
    if (isRight(this.p, this.e, i)) {
      note = `${this.e[i]} is right: ${compounds(this.e, i).join(" and ")}.`
      this.cur = this.firstOpen(i + 1)
    } else if (isFull(this.p, this.e, i)) {
      note = `${this.e[i]} doesn't fit between ${this.e[i - 1]} and ${this.e[i + 1]}. Press Backspace to change it.`
    }
    this.render()
    this.focusWord()
    this.say(note ? `${note} ${isRight(this.p, this.e, i) ? this.where() : ""}`.trim() : this.where())
  }

  // ---- helpers -----------------------------------------------------------------------------------------------
  /** The first hidden word from `from` on (wrapping round) that isn't right yet. */
  private firstOpen(from: number): number {
    const h = hidden(this.p)
    const open = (i: number): boolean => !isRight(this.p, this.e, i)
    return h.find((i) => i >= from && open(i)) ?? h.find(open) ?? h[0]!
  }
  private wordEl(i: number): HTMLElement | null { return this.chain.querySelector<HTMLElement>(`[data-w="${i}"]`) }
  private where(): string {
    const i = this.cur
    const w = this.p.words[i]!
    const typed = this.e[i]!
    return `Word ${hidden(this.p).indexOf(i) + 1} of ${hidden(this.p).length}, after ${this.e[i - 1]}: ${w.length} letters, ${typed.length > 1 ? `you have ${typed}` : `starts with ${typed}`}.`
  }
  private focusWord(): void {
    const el = this.wordEl(this.cur)
    this.chain.querySelectorAll<HTMLElement>("[data-w]").forEach((x) => { if (x.tagName === "BUTTON") x.tabIndex = x === el ? 0 : -1 })
    if (this.chain.contains(document.activeElement)) el?.focus({ preventScroll: true })
    if (document.activeElement === this.input) this.placeInput()
    this.resetInput()
  }

  // ---- rendering ---------------------------------------------------------------------------------------------
  private rebuild(): void {
    this.chain.innerHTML = chainHtml(this.p)
    this.root.dataset.puzzleId = this.p.id
    document.querySelectorAll<HTMLElement>("[data-wc-levels] [data-level]").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.level === this.p.level)))
    this.render()
  }

  private render(): void {
    const solved = isSolved(this.p, this.e)
    gameProgress({ key: this.saveKey(this.p.level), ruleset: `word-chain:${this.p.level}`, id: `${this.p.id}#${this.attempt}`, started: this.undo.length > 0, won: solved })
    const last = this.p.words.length - 1
    this.p.words.forEach((w, i) => {
      const el = this.wordEl(i)
      if (!el) return
      const typed = this.e[i]!
      const right = isRight(this.p, this.e, i)
      const wrong = !right && isFull(this.p, this.e, i)
      el.querySelectorAll<HTMLElement>(".wc-cell").forEach((c, k) => { c.textContent = typed[k] ?? "" })
      el.classList.toggle("is-current", !solved && i === this.cur)
      el.classList.toggle("is-right", right && i > 0 && i < last)
      el.classList.toggle("is-wrong", wrong)
      if (el.tagName === "BUTTON") el.setAttribute("aria-label", `Word ${hidden(this.p).indexOf(i) + 1}, ${w.length} letters: ${right ? `${typed}, right` : wrong ? `${typed}, doesn't fit` : `${typed}, ${w.length - typed.length} to go`}`)
      if (this.input && i === this.cur) this.input.setAttribute("aria-label", `Type a letter: ${el.getAttribute("aria-label")}`)
      const join = this.chain.querySelector<HTMLElement>(`[data-join="${i}"]`)
      if (join) {
        const both = isRight(this.p, this.e, i) && isRight(this.p, this.e, i + 1)
        join.textContent = both ? (this.e[i]! + this.e[i + 1]!).toLowerCase() : ""
      }
    })
  }

  private say(msg: string): void { if (this.status) this.status.textContent = msg }
}

/** The chain, word by word; the same markup as games/boards/_word_chain.html.erb. */
export function chainHtml(p: Puzzle): string {
  const last = p.words.length - 1
  return p.words.map((w, i) => {
    const cells = [...w].map((ch, k) => `<span class="wc-cell${k === 0 ? " is-given" : ""}">${i === 0 || i === last || k === 0 ? ch : ""}</span>`).join("")
    const word = i === 0 || i === last
      ? `<div class="wc-word is-end" data-w="${i}"><span class="wc-cells" style="--n: ${w.length}">${cells}</span></div>`
      : `<button type="button" class="wc-word" data-w="${i}" tabindex="-1"><span class="wc-cells" style="--n: ${w.length}">${cells}</span></button>`
    const join = i < last ? `<p class="wc-join" aria-hidden="true"><span class="wc-plus">+</span><span class="wc-compound" data-join="${i}"></span></p>` : ""
    return `<li>${word}${join}</li>`
  }).join("")
}

function readPref(): WCLevel | null {
  try {
    const v = window.localStorage.getItem(PREF_KEY)
    return v === "easy" || v === "medium" || v === "hard" ? v : null
  } catch { return null }
}
function writePref(l: WCLevel): void {
  try { window.localStorage.setItem(PREF_KEY, l) } catch { /* ignore */ }
}
const cap = (s: string): string => s[0]!.toUpperCase() + s.slice(1)
